"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import TrustpilotWidget from "@/components/ui/TrustpilotWidget";

interface HeroDictionary {
  home?: {
    hero?: {
      highlights?: string[];
    };
  };
}

interface HeroContentProps {
  title: string;
  subtitle: string;
  btnText: string;
  btnLink: string;
  secondaryBtnText?: string;
  secondaryBtnLink?: string;
  imageSrc: string;
  imageAlt: string;
  dict?: HeroDictionary;
  lang?: string;
}

export default function HeroContent({
  title,
  subtitle,
  btnText,
  btnLink,
  secondaryBtnText,
  secondaryBtnLink,
  imageSrc,
  imageAlt,
  dict,
  lang = "tr",
}: HeroContentProps) {
  const highlights = dict?.home?.hero?.highlights || [];

  return (
    <div className="relative overflow-hidden rounded-[30px] border border-white/60 bg-[#0e2743] shadow-[0_30px_80px_rgba(5,30,60,0.24)]">
      <div className="absolute inset-0">
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          priority
          fetchPriority="high"
          quality={75}
          className="object-cover"
          sizes="100vw"
        />
      </div>

      {/* Mobile: vertical scrim keeps top + bottom text readable. Desktop: side scrim. */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,30,60,0.72)_0%,rgba(5,30,60,0.30)_38%,rgba(5,30,60,0.55)_72%,rgba(5,30,60,0.92)_100%)] lg:bg-[linear-gradient(90deg,rgba(5,30,60,0.82)_0%,rgba(5,30,60,0.68)_34%,rgba(5,30,60,0.18)_68%,rgba(5,30,60,0.10)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(117,205,255,0.24),transparent_28%)]" />

      <div className="relative flex min-h-[540px] flex-col px-5 pb-7 pt-7 sm:min-h-[600px] sm:px-7 sm:pb-8 sm:pt-8 lg:min-h-[540px] lg:px-8 lg:pb-8 lg:pt-8 xl:min-h-[650px]">
        <div className="max-w-[620px] pt-2 sm:pt-4 lg:pt-6">
            <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 shadow-sm backdrop-blur-md">
              <TrustpilotWidget
                locale={lang === "tr" ? "tr-TR" : "en-US"}
                reviewLanguages={lang === "tr" ? "tr" : "en"}
                styleHeight="24px"
              />
            </div>

            <h1 className="mt-5 max-w-[13ch] text-[2.5rem] font-bold leading-[0.95] tracking-[-0.05em] text-white sm:max-w-[10.5ch] sm:text-[3.45rem] sm:tracking-[-0.06em] lg:text-[4.15rem] xl:text-[4.7rem]">
              {title}
            </h1>

            <p className="mt-4 max-w-[52ch] text-[15px] leading-6 text-white/85 sm:text-[16px]">
              {subtitle}
            </p>
        </div>

        <div className="mt-auto flex max-w-[760px] flex-col items-start gap-4 pt-10 sm:pt-12">
          {highlights.length > 0 ? (
            <div className="flex flex-wrap gap-2.5">
              {highlights.map((item) => (
                <div
                  key={item}
                  className="inline-flex items-center rounded-full border border-white/18 bg-white/8 px-3.5 py-2 text-[12px] font-medium text-white/88 backdrop-blur-sm"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4 text-[#7fd2ff]" />
                  {item}
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:w-auto">
            <Link
              href={btnLink}
              className="inline-flex min-w-0 flex-1 items-center justify-center rounded-full bg-white px-4 py-3 text-center text-[13px] font-semibold text-[#051e3c] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f2f7fb] sm:min-w-[190px] sm:flex-none sm:px-5 sm:text-[14px]"
            >
              {btnText}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            {secondaryBtnText && (
              <Link
                href={secondaryBtnLink || "#services"}
                className="inline-flex min-w-0 flex-1 items-center justify-center rounded-full border border-white/26 bg-white/10 px-4 py-3 text-center text-[13px] font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/16 sm:min-w-[165px] sm:flex-none sm:px-5 sm:text-[14px]"
              >
                {secondaryBtnText}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
